import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/mental-health-word-cloud-template-mental-support-concept-vector-tagcloud-background_616200-7363.jpg';

function MentalHealthAndSubstanceAbuse() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (match the extended pages you liked)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Get Help Now',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Immediate danger:', 'Call 911.'],
            ['Suicidal thoughts or crisis:', 'Call or text 988 to reach the Suicide & Crisis Lifeline (24/7).'],
            ['After-hours access:', 'Use 988 for guidance to local mobile crisis/walk-in options.'],
          ].map(([k, v], i) => (
            <li key={`now-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'How to Start Care',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Self-referral:', 'Request an intake—no referral required. Brief screening → intake appointment → service plan.'],
            ['What to bring:', 'Photo ID (if available), insurance card (if any), list of meds, prior records if you have them.'],
            ['If you’re helping someone:', 'You can call to ask about releases, eligibility, and appointment options.'],
          ].map(([k, v], i) => (
            <li key={`start-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Core Services Offered',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Outpatient therapy:', 'Individual, family, and group counseling (CBT, DBT, trauma-informed approaches).'],
            ['Psychiatric care:', 'Evaluation, medication management, and monitoring.'],
            ['Care coordination:', 'Linkage to housing, employment, benefits, and community supports.'],
            ['Co-occurring care:', 'Integrated treatment for mental health and substance use together.'],
          ].map(([k, v], i) => (
            <li key={`svc-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Substance Use Treatment Options',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Medication-assisted treatment (MAT):', 'Buprenorphine, naltrexone, or methadone (via approved providers).'],
            ['Outpatient/IOP:', 'Structured therapy several times per week while living at home.'],
            ['Residential & detox:', 'Short-term stabilization and longer programs when clinically indicated.'],
            ['Harm reduction:', 'Naloxone access, safer-use education, and fentanyl test strips where available.'],
          ].map(([k, v], i) => (
            <li key={`sud-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Crisis Services',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Mobile crisis response:', 'On-scene assessment and de-escalation when available.'],
            ['Walk-in crisis centers:', 'Short wait, evaluation, safety planning, and referrals.'],
            ['23-hour observation:', 'Brief stabilization to avoid ER admissions when appropriate.'],
          ].map(([k, v], i) => (
            <li key={`crisis-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Access, Eligibility, and Payment',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Who can receive services:', 'Adults, youth, and families—self-referral or provider referral.'],
            ['Insurance & assistance:', 'Most plans accepted (including public programs); sliding-fee options may be available.'],
            ['Transportation & scheduling:', 'Ask about telehealth, extended hours, and transit options.'],
          ].map(([k, v], i) => (
            <li key={`pay-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Telehealth & Accessibility',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Video/phone visits:', 'Secure teletherapy and med-management when clinically appropriate.'],
            ['Language access:', 'Interpreter services and translated materials upon request.'],
            ['Disability access:', 'ADA-compliant facilities; reasonable accommodations available.'],
          ].map(([k, v], i) => (
            <li key={`access-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Support for Families & Caregivers',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Education & groups:', 'Psychoeducation and skills groups to support recovery at home.'],
            ['Release of information:', 'With consent, staff can coordinate care and share updates.'],
            ['Youth services:', 'School coordination and age-appropriate therapy; guardians invited to participate.'],
          ].map(([k, v], i) => (
            <li key={`fam-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Aftercare & Relapse Prevention',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Safety & relapse plans:', 'Identify triggers, coping skills, and support contacts; carry naloxone if at risk.'],
            ['Peer support:', 'Certified peer specialists and mutual-aid groups (e.g., NA/AA, SMART).'],
            ['Follow-ups:', 'Scheduled check-ins, medication refills, and fast-track re-entry if symptoms return.'],
          ].map(([k, v], i) => (
            <li key={`after-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Privacy & Rights',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Confidentiality:', 'Your records are protected; info is shared only with your consent or as required by law.'],
            ['Informed consent:', 'You’ll review risks/benefits and can ask questions before starting treatment.'],
            ['Complaints & appeals:', 'You have the right to file grievances and request a second opinion.'],
          ].map(([k, v], i) => (
            <li key={`rights-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Mental Health and Substance Abuse Services" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Mental Health and Substance Abuse Services</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                Compassionate, practical care for depression, anxiety, trauma, and substance use—ranging from counseling
                and medication management to crisis response, harm-reduction, and coordinated recovery supports.
              </p>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We deliver respectful, evidence-based care—prioritizing safety, access, and continuity—so people can
                stabilize, recover, and maintain wellness.
              </p>
            </div>

            <div>
              <Link to="/department/health">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default MentalHealthAndSubstanceAbuse;
